import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { API } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Clock, Loader2, Share2, CheckCircle2, Download } from 'lucide-react';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '');
const PROSE = '[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-[#2a3e5d] [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-[#2a3e5d] [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:text-slate-700 [&_p]:leading-[1.8] [&_p]:mb-4 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-6 [&_ol]:pl-6 [&_ul]:mb-5 [&_ol]:mb-5 [&_li]:text-slate-700 [&_li]:leading-[1.75] [&_li]:mb-1.5 [&_a]:text-[#1590d4] [&_a]:font-medium [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-[#2a3e5d] [&_strong]:text-[#1e293b]';

export default function ArticlePage() {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth();
  const [art, setArt] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setArt(null);
    axios.get(`${API}/hub/articles/${slug}`).then((r) => setArt(r.data)).catch(() => setMissing(true));
    window.scrollTo(0, 0);
  }, [slug]);

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: art.title, url: window.location.href });
      else { await navigator.clipboard.writeText(window.location.href); toast.success('Link copied'); }
    } catch { /* user cancelled */ }
  };

  const html = art ? DOMPurify.sanitize((art.content || '').replace(/^\s*<h1[^>]*>.*?<\/h1>/is, '')) : '';
  const url = typeof window !== 'undefined' ? window.location.href : '';
  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="article-page">
      {art && (
        <Helmet>
          <title>{art.meta_title || art.title}</title>
          <meta name="description" content={art.meta_description || art.excerpt} />
          <link rel="canonical" href={url} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={art.meta_title || art.title} />
          <meta property="og:description" content={art.meta_description || art.excerpt} />
          <meta property="og:url" content={url} />
          {art.cover_url && <meta property="og:image" content={art.cover_url} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={art.meta_title || art.title} />
          <meta name="twitter:description" content={art.meta_description || art.excerpt} />
          <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'Article', headline: art.title, description: art.meta_description || art.excerpt, datePublished: art.published_at, author: { '@type': 'Organization', name: art.author || 'Common Impact' } })}</script>
        </Helmet>
      )}
      <Header />
      {missing ? (
        <div className="flex-1 flex flex-col items-center justify-center py-24 gap-4"><p className="text-slate-600">This article could not be found.</p><Button asChild variant="outline"><Link to="/knowledge-hub">Back to Knowledge Hub</Link></Button></div>
      ) : !art ? (
        <div className="flex-1 flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
      ) : (
        <main className="flex-1">
          <header className="bg-[#F7F9FC] border-b border-slate-200">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
              <Link to="/knowledge-hub" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#2a3e5d]" data-testid="article-back-link"><ArrowLeft className="h-4 w-4" /> Knowledge Hub</Link>
              <span className="block mt-6 text-xs uppercase tracking-[0.16em] font-semibold text-[#20acf6]">{art.category}</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2a3e5d] mt-3 leading-[1.12]" data-testid="article-title">{art.title}</h1>
              <p className="text-base md:text-lg text-slate-600 mt-4">{art.excerpt}</p>
              <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-slate-500">
                <span>{art.author}</span><span>{fmtDate(art.published_at)}</span>
                <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{art.read_minutes || 5} min read</span>
                <button onClick={share} className="flex items-center gap-1 text-[#2a3e5d] hover:text-[#20acf6]" data-testid="article-share-btn"><Share2 className="h-4 w-4" /> Share</button>
              </div>
            </div>
          </header>
          <article className={`max-w-3xl mx-auto px-4 sm:px-6 py-10 text-base ${PROSE}`} data-testid="article-body" dangerouslySetInnerHTML={{ __html: html }} />
          <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12">
            <div className="rounded-2xl bg-[#2a3e5d] p-8 flex flex-col md:flex-row md:items-center gap-6" data-testid="article-cta">
              <div className="flex-1">
                <p className="text-white font-bold text-xl">Turn this knowledge into a certificate</p>
                <p className="text-white/70 text-sm mt-1 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#02d1d4]" /> CPD-accredited online courses with verified certificates</p>
              </div>
              <div className="flex gap-3 flex-wrap">
                <Button asChild className="bg-[#02d1d4] hover:bg-[#02bfc2] text-[#1e293b] rounded-xl"><Link to={isAuthenticated ? '/courses?price=free' : '/register'}>Try free courses</Link></Button>
                <Button asChild variant="outline" className="rounded-xl bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white"><Link to="/knowledge-hub?tab=downloads"><Download className="h-4 w-4 mr-1.5" />Free checklists</Link></Button>
              </div>
            </div>
          </section>
          {art.related?.length > 0 && (
            <section className="bg-[#F7F9FC] border-t border-slate-200">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
                <h2 className="text-lg font-bold text-[#2a3e5d] mb-5">Related reading</h2>
                <div className="grid sm:grid-cols-3 gap-5">
                  {art.related.map((r) => (
                    <Link key={r.id} to={`/knowledge-hub/${r.slug}`} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid={`related-${r.slug}`}>
                      <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#20acf6]">{r.category}</span>
                      <p className="font-semibold text-[#2a3e5d] mt-1.5 leading-snug">{r.title}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
      )}
      <Footer />
    </div>
  );
}
